import HistoryIconLink from "../../../components/change-history/HistoryIconLink";
import { PencilSquareIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import { StopFieldSourcePill } from "../../../components/gtfs/StopFieldSource";
import HeaderPage from "../../../components/lines-managements/header/HeaderPage";
import Table, { Row } from "../../../components/ui/table";
import Pagination from "../../../components/ui/table/Pagination";
import Can from "../../../components/users/permissions/Can";
import useCan from "../../../components/users/permissions/useCan";
import usePageIndex from "../../../hooks/usePageIndex";
import { useSearch } from "../../../hooks/useSearch";
import { IGTFSStation } from "../../../interfaces/endpoints/IGTFS.types";
import { getGTFSStations } from "../../../services/GTFS.service";

const COLUMNS = ["ID", "Emri", "Gjerësia", "Gjatësia", "Burimi", "", "Veprimet"];
const PAGE_SIZE = 10;

// Values that follow the station are shown muted; values edited for the feed keep the normal colour.
const cell = (value: string | number, follows: boolean) => (
  <span className={follows ? "text-gray-500" : undefined}>{value}</span>
);

// Where the feed takes the name and the location from, as two small pills.
const sources = (row: IGTFSStation) => (
  <span className="inline-flex flex-wrap gap-1">
    <StopFieldSourcePill compact state={row.isNameEdited ? "edited" : "station"} label="Emri" />
    <StopFieldSourcePill compact state={row.isLocationEdited ? "edited" : "station"} label="Vendndodhja" />
  </span>
);

const GTFSStations: React.FC = () => {
  const can = useCan("gtfs.create");
  const { response, loading, handlePageChange, page } = useSearch(getGTFSStations, {
    withQuerySearch: true
  });

  const { pageIndex } = usePageIndex();

  return (
    <>
      <HeaderPage {...(can && {
          link: {
            to: "create",
            children: "Shto stacion të ri",
            state: ["Menaxhimi i GTFS", "Stacionet", "Shto"]
          }
        })}/>
      <div className="flex flex-col" style={{ height: "calc(100vh - 11rem)" }}>
        <Table titles={COLUMNS} style="hover" loading={loading}>
          {!loading && response?.data?.result?.length ? (
            response.data.result.map((row) => (
              <Row
                key={row.id}
                id={row.id}
                name={cell(row.name, !row.isNameEdited)}
                latitude={cell(row.latitude, !row.isLocationEdited)}
                longitude={cell(row.longitude, !row.isLocationEdited)}
                source={sources(row)}
                history={<HistoryIconLink entityName="GTFSStop" entityId={row.id} />}
                actions={
                  <Can permissions="gtfs.update">
                    <Link
                      className="p-2.5 md:p-1"
                      state={{
                        crumbs: ["Menaxhimi i GTFS", "Stacionet", "Ndrysho"],
                        page: pageIndex
                      }}
                      to={`edit/${row.id}`}
                    >
                      <PencilSquareIcon className="w-5 h-5 md:w-4" />
                    </Link>
                  </Can>
                }
              />
            ))
          ) : (
            <tr>
              <td colSpan={COLUMNS.length} className="text-center py-4">
                {loading ? "Duke u ngarkuar..." : "Nuk u gjet asnjë stacion"}
              </td>
            </tr>
          )}
        </Table>
        <Pagination
          onPageChange={handlePageChange}
          totalCount={response?.data?.totalCount || 0}
          length={response?.data?.result?.length || 0}
          page={page}
          pageSize={PAGE_SIZE}
        />
      </div>
    </>
  );
};

export default GTFSStations;
